<template>
    <el-form :inline="true">
        <el-form-item class="small" label="Font">
            <el-select
                @change="onChangeFont"
                @visible-change="
                    (value) => {
                        if (value) onDropdownOpen();
                    }
                "
                placeholder="Select"
                style="width: 250px"
                v-model="fontForm.font"
                popper-class="font-select-dropdown"
            >
                <el-option
                    v-for="font in availableFonts"
                    :key="font"
                    :label="$options.filters.capitalize_all(font)"
                    :value="font"
                    :style="getFontStyle(font)"
                ></el-option>
            </el-select>
        </el-form-item>
        <el-form-item class="small" label="Font Size">
            <el-select
                @change="onChangeFontSize"
                placeholder="Select"
                style="width: 78px"
                v-model="fontForm.font_size"
            >
                <el-option
                    v-for="size in fontSizes"
                    :key="size"
                    :label="size + 'pt'"
                    :value="size"
                    :style="{ 'font-size': size + 'pt' }"
                >
                </el-option>
            </el-select>
        </el-form-item>
    </el-form>
</template>

<script>
import * as Fonts from "@/constants/calendar-fonts";
import FontService from "@/services/font-service";
import FontLoader from "@/services/FontLoader";
import { CalendarCalculator } from "@/utils/calendar";
import { mapState } from "vuex";

export default {
    props: {
        form: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            Fonts,
            previousFont: null,
            previousFontSize: null,
            fontForm: new Form({
                font: null,
                font_size: null
            }),
            dropdownVisible: false
        };
    },
    computed: {
        ...mapState({
            fontSizes: (state) => state.config.settings.font.sizes
        }),
        calendarCalculator() {
            return new CalendarCalculator({
                month: this.form.month,
                year: this.form.year,
                font: this.fontForm.font,
                font_size: this.fontForm.font_size
            });
        },
        availableFonts() {
            // Define the font order explicitly by font family and weight
            const orderedFonts = [
                // Classic family
                this.Fonts.HELVETICA,
                this.Fonts.HELVETICA_BOLD,
                this.Fonts.HELVETICA_NARROW,
                this.Fonts.HELVETICA_NARROW_BOLD,

                // Modern family
                this.Fonts.AVENIR_NEXT_LIGHT,
                this.Fonts.AVENIR_NEXT_MEDIUM,
                this.Fonts.AVENIR_NEXT_BOLD,
                this.Fonts.AVENIR_NEXT_CONDENSED_MEDIUM,
                this.Fonts.AVENIR_NEXT_CONDENSED_BOLD,

                // Geometric family
                this.Fonts.BARLOW_CONDENSED_MEDIUM,
                this.Fonts.BARLOW_CONDENSED_BOLD,

                // Traditional family
                this.Fonts.CRIMSONPRO_MEDIUM,
                this.Fonts.CRIMSONPRO_BOLD
            ];

            // Apply the same filtering logic as before
            if (!this.fontForm.font) {
                return orderedFonts.filter(
                    (font) => !font.includes("Classic Narrow")
                );
            }

            return this.fontForm.font.includes("Classic Narrow")
                ? orderedFonts
                : orderedFonts.filter(
                      (font) => !font.includes("Classic Narrow")
                  );
        }
    },
    mounted() {
        _.extend(this.fontForm, _.pick(this.form, ["font", "font_size"]));

        // Load current font immediately
        if (this.fontForm.font) {
            FontLoader.loadFont(this.fontForm.font);
        }
    },
    watch: {
        "form.font"(value) {
            this.fontForm.font = value;
        },
        "form.font_size"(value) {
            this.fontForm.font_size = value;
        }
    },
    methods: {
        // New method to handle dropdown opening
        onDropdownOpen() {
            this.dropdownVisible = true;
            // Preload lightweight versions of fonts for the dropdown
            FontLoader.preloadDropdownFonts();
        },

        onChangeFont(font) {
            // Load the selected font
            FontLoader.loadFont(font).then(() => {
                this.checkCustomTextLines();
            });
        },
        onChangeFontSize(font_size) {
            this.checkCustomTextLines();
        },
        checkCustomTextLines() {
            let count = 0;
            this.form.location_keys.forEach((item) => {
                count +=
                    this.calendarCalculator.getLocationKeyValueLines(
                        item
                    ).length;
            });
            count += this.calendarCalculator.getCustomTextLines(
                this.form.custom_text
            ).length;
            if (count > this.calendarCalculator.custom_text_max_lines) {
                this.$alert(
                    "Please update your custom text/location keys in order to fit with the new font.",
                    "Oops!",
                    {
                        type: "warning",
                        callback: () => {
                            _.extend(
                                this.fontForm,
                                _.pick(this.form, ["font", "font_size"])
                            );
                        }
                    }
                );
            } else {
                _.extend(
                    this.form,
                    _.pick(this.fontForm, ["font", "font_size"])
                );
            }
        },
        getFontStyle(font) {
            return {
                "font-family": `"${font}", sans-serif`,
                "font-size": "12pt",
                "font-weight": FontService.getFontWeightFromCSS(font)
            };
        }
    }
};
</script>

<style>
.font-select-dropdown {
    max-height: none !important;
    width: 250px !important;
}

/* This targets the dropdown options container */
.font-select-dropdown .el-select-dropdown__wrap {
    max-height: 420px;
}
</style>
